import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { variableFontRegular } from 'styles/theme/text';
import { useWindowSize } from 'shared/hooks/useWindowSize';

import { ISearchConfig } from 'homepage/types/SectionSearchConfig';
import { slugify } from 'shared/utils/consumerSearchSlugify';

interface ICuratedLinkTableRow {
  id?: number;
  title: string;
  count: number;
  location?: string;
  onClick: () => void;
  searchConfig: ISearchConfig;
  year?: number | string;
  uuid?: string;
}

export default function CuratedLinkTableRow({
  id,
  title,
  count,
  location = '',
  onClick,
  searchConfig,
  year = null,
  uuid,
}: ICuratedLinkTableRow) {
  const { isMobile } = useWindowSize();
  const { filterValue } = searchConfig || {};
  const linkUrl = `/brand/${slugify(filterValue)}`;
  const shortLocation = location ? location.split(',')[1] : '';
  const router = useRouter();

  const handleClick = () => {
    onClick();
    void router.push(linkUrl);
  };

  return (
    <CuratedLinkTableRow.Row
      data-qa="curated_link_table_row"
      key={uuid || id}
      onClick={handleClick}
    >
      <CuratedLinkTableRow.NameCell>
        <Link href={linkUrl} passHref prefetch={false} legacyBehavior>
          <CuratedLinkTableRow.NameLink data-qa="curated_link_table_row_link">
            {title}
          </CuratedLinkTableRow.NameLink>
        </Link>
      </CuratedLinkTableRow.NameCell>
      <CuratedLinkTableRow.YearCell data-qa="curated_link_table_row_year">
        {year}
      </CuratedLinkTableRow.YearCell>
      <CuratedLinkTableRow.LocationCell data-qa="curated_link_table_row_location">
        {isMobile ? shortLocation : location}
      </CuratedLinkTableRow.LocationCell>
      <CuratedLinkTableRow.DataContainer data-qa="curated_link_table_row_count">
        {count?.toLocaleString()}
      </CuratedLinkTableRow.DataContainer>
    </CuratedLinkTableRow.Row>
  );
}

CuratedLinkTableRow.Row = styled.tr`
  display: inline-block;
  border-bottom: ${({ theme }) => theme.borders.white};
  width: 322px;

  &:hover {
    cursor: pointer;

    td {
      text-decoration: underline;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 639px;
  }
`;

CuratedLinkTableRow.DataContainer = styled.td`
  display: inline-block;
  ${variableFontRegular({ size: '11.5px', height: '11px' })}
  text-align: left;
  padding: 16px 0px;
  width: 42px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 80px;
  }
`;

CuratedLinkTableRow.NameCell = styled(CuratedLinkTableRow.DataContainer)`
  width: 169px;
  padding-right: 40px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 221px;
  }
`;

CuratedLinkTableRow.YearCell = styled(CuratedLinkTableRow.DataContainer)`
  width: 43px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 111px;
  }
`;

CuratedLinkTableRow.LocationCell = styled(CuratedLinkTableRow.DataContainer)`
  width: 67px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 221px;
  }
`;

CuratedLinkTableRow.NameLink = styled.a`
  text-decoration: none;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;
